<script lang="ts" module>
  import type { HTMLButtonAttributes, MouseEventHandler } from 'svelte/elements';
  import type { Snippet } from 'svelte';

  import { ToolTip } from './tooltip';

  interface ToolTipProps {
    label: string;
    size?: 'big' | 'small';
    active?: boolean;
    anchor?: 'top' | 'bottom' | 'left' | 'right';
  }

  type AccessabilityGuard =
    | {
        children: Snippet;
        label?: string;
        tooltip?: ToolTipProps | string;
      }
    | {
        children?: Snippet;
        label: string;
        tooltip?: ToolTipProps | string;
      }
    | {
        children?: Snippet;
        label?: string;
        tooltip: ToolTipProps | string;
      };

  interface CommonButtonProps extends Omit<HTMLButtonAttributes, 'class' | 'children' | 'label'> {
    selected?: boolean;
    class?: string;
  }

  export type BaseBtnProps = CommonButtonProps & AccessabilityGuard;
</script>

<script lang="ts">
  let {
    tooltip,
    disabled,
    selected,
    onclick,
    id: propsId,
    label,
    children,
    ...props
  }: BaseBtnProps = $props();

  let tooltipProps = $derived(
    typeof tooltip === 'string' ?
      { label: tooltip, active: selected }
    : tooltip && {
        ...tooltip,
        active: tooltip.active ?? selected,
        size: tooltip.size ?? 'small',
      },
  );
  // proprity of aria-label
  // 1. use props.aria-label
  // 2. children & label => label is an addition to be used as aria-label
  // 3. use tooltip.label, if nothing else is available
  // 4. either children are a11y compliant or we have a label as button text (aria compliant)
  let ariaLabel = $derived(props['aria-label'] ?? (children && label) ?? tooltipProps?.label);
  // a selected button can not be clicked again (but it is not design relevant!!!)
  let isDisabled = $derived(!!disabled || selected);
  let handleClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    if (!isDisabled) {
      onclick?.(event);
    }
  };
  let id = $derived(propsId || Math.random().toString(36).substring(2, 15));
</script>

{#if tooltipProps}
  <ToolTip {...tooltipProps}>
    {#snippet trigger(triggerProps)}
      <button
        type="button"
        aria-label={ariaLabel}
        {...triggerProps}
        {...props as HTMLButtonAttributes}
        onclick={handleClick}
        disabled={isDisabled}
      >
        {@render children?.()}
      </button>
    {/snippet}
  </ToolTip>
{:else}
  <button
    type="button"
    aria-label={ariaLabel}
    {id}
    {...props as HTMLButtonAttributes}
    onclick={handleClick}
    disabled={isDisabled}
  >
    {@render children?.()}
  </button>
{/if}
